import { createSelector } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectIsCurrentUrlIndexable } from './selectIsCurrentUrlIndexable';

const selectCurrentPage = (state: ReduxApplicationState) => state.productsQuery?.page ?? 1;

/**
 * The taxonomy PLP's `<meta name="robots">` content, or `null` for no tag.
 *
 * Driven by whether the current URL has its own indexable content entry in the
 * neighbourhood map (`content.entries`) — the single source of truth. "Opening
 * up" a URL for SEO is done by marking its Contentstack entry `indexable`.
 *
 * - URL with an exact indexable entry, page 1: no tag (`index, follow`).
 * - Same URL on `page > 1`: `noindex, follow` (paginated views are not indexed,
 *   but their links are followed).
 * - Any other URL — no exact indexable entry (long-tail, an `indexable: false`
 *   entry, or an unavailable map): `noindex, nofollow`. The canonical is
 *   self-referencing by default (see {@link selectTaxonomyCanonical}), so
 *   pairing it with `noindex` has no cross-pointing target to harm — the lone
 *   exception is a deliberately authored `seo.canonical_url_override`.
 */
export const selectTaxonomyRobotsContent = createSelector(
  [selectIsCurrentUrlIndexable, selectCurrentPage],
  (isIndexable, page): string | null => {
    if (!isIndexable) {
      return 'noindex, nofollow';
    }

    if (page > 1) {
      return 'noindex, follow';
    }

    return null;
  },
);
